'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { Dialog, dialog } from '@flipster/react/design-system/dialog/index';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { divider, flex, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

import { LeverageBadge, OrderSideBadge } from '../misc/badge';
import { SymbolDisplay } from '../misc/display';
import { positionCloseModalBunja } from './state';

interface Props {
  mode: 'full' | 'partial';
}

export const CloseDetail = ({ mode }: Props) => {
  const t = useTranslations();
  const { positionAtom, limitPriceAtom, tabAtom, bboPriceAtom, inputQtyAtom, instrumentAtom } =
    useBunja(positionCloseModalBunja);
  const tab = useAtomValue(tabAtom);
  const position = useAtomValue(positionAtom);
  const limitPrice = useAtomValue(limitPriceAtom);
  const bboPrice = useAtomValue(bboPriceAtom);
  const inputQty = useAtomValue(inputQtyAtom);
  const instrument = useAtomValue(instrumentAtom);

  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  const partialPnl = (() => {
    switch (mode) {
      case 'full':
        return Num(position.unrealizedPnl);
      case 'partial': {
        if (tab === 'market') {
          const side = position.side === 'Long' ? '1' : '-1';
          const positionUnrealizedPnl = Num(bboPrice)
            .minus(position.avgEntryPrice)
            .multipliedBy(inputQty)
            .multipliedBy(side);
          const optionPositionUnrealizedPnls = position.options.map((option) => {
            const direction = option.direction === 'Call' ? '1' : '-1';
            const pnl = Num(direction)
              .multipliedBy(Num(bboPrice).minus(option.strike))
              .multipliedBy(BigNumber.max(Num(inputQty).minus(Num(position.quantity).minus(option.qty)), 0));
            return BigNumber.max(pnl, 0);
          });
          const sumOfOptionPositionUnrealizedPnls = BigNumber.sum(...optionPositionUnrealizedPnls, 0);
          return positionUnrealizedPnl.plus(sumOfOptionPositionUnrealizedPnls);
        }
        // limit
        // https://www.notion.so/prextech/Limit-Order-Close-Position-29ceb32ad22780e6bb06ebba36665b0c?source=copy_link#2b0eb32ad2278039a7b9d3d146d2b962
        const side = position.side === 'Long' ? '1' : '-1';
        const positionUnrealizedPnl = Num(Num(limitPrice).minus(position.avgEntryPrice))
          .multipliedBy(inputQty)
          .multipliedBy(side);
        const optionPositionUnrealizedPnls = position.options.map((option) => {
          const direction = option.direction === 'Call' ? '1' : '-1';
          const pnl = Num(direction)
            .multipliedBy(Num(limitPrice).minus(option.strike))
            .multipliedBy(BigNumber.max(Num(inputQty).minus(Num(position.quantity).minus(option.qty)), 0));
          return BigNumber.max(pnl, 0);
        });
        const sumOfOptionPositionUnrealizedPnls = BigNumber.sum(...optionPositionUnrealizedPnls, 0);

        return positionUnrealizedPnl.plus(sumOfOptionPositionUnrealizedPnls);
      }
      default: {
        return Num('0');
      }
    }
  })();

  return (
    <div
      className={css({
        p: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        borderRadius: 8,
      })}
    >
      <dl
        className={stack({
          gap: 8,
          '& dt': {
            display: 'flex',
            alignItems: 'center',
            textStyle: 'Text/L/Regular',
            color: 'content.default.level-2',
          },
          '& dd': {
            textStyle: 'Text/L/Monospace/Regular',
            color: 'content.default.level-5',
          },
        })}
      >
        <div className={flex({ justify: 'space-between', alignItems: 'center' })}>
          <dt>
            {t('Position.r250401.Estimated_pnl')}
            <IconButton
              type='button'
              hierarchy='quaternary'
              icon={<InformationCircleIcon size={20} color={token('colors.content.default.level-2')} />}
              onClick={() => {
                dialog.open({
                  title: {
                    label: t('Position.r250401.Estimated_pnl'),
                  },
                  content: t('Trade.r251119.Estimated_pnl_for_amount_closing_trading_fees_not_included'),
                  buttons: {
                    primary: <Dialog.Button hierarchy='primary' label={t('Common.r250401.Ok')} />,
                  },
                });
              }}
            />
          </dt>
          <dd className={flex({ gap: 2 })}>
            <Formatted.Number value={partialPnl} precision={quoteCurrencyPrecision} withPlus withColor />
            {mode === 'full' && (
              <Formatted.Percent value={position.calcUnrealizedPnlPercent} withColor withPlus withParentheses />
            )}
          </dd>
        </div>
        <div
          className={divider({ orientation: 'horizontal', color: 'border.default.level-1', my: 8, h: 1 })}
          role='separator'
        />
        <div className={flex({ gap: 8 })}>
          <SymbolDisplay
            symbol={position.symbol}
            mainLabel={
              <SymbolDisplay.Label
                value='shortName'
                css={{ textStyle: 'Title/XS', color: 'content.default.level-5' }}
              />
            }
          />
          <div className={flex({ gap: 4 })}>
            <OrderSideBadge orderSide={position.side} />
            <LeverageBadge leverage={position.initLeverage} marginType={position.marginType} />
          </div>
        </div>
        <div className={flex({ justify: 'space-between' })}>
          <dt>
            {t('Trade.r250401.Market_price')} {`(${instrument.quoteCurrency})`}
          </dt>
          <dd>
            <Formatted.Number value={bboPrice} precision={quoteCurrencyPrecision} />
          </dd>
        </div>
        <div className={flex({ justify: 'space-between' })}>
          <dt>
            {t('Assets.r250401.Entry_price')} {`(${instrument.quoteCurrency})`}
          </dt>
          <dd>
            <Formatted.Number value={position.avgEntryPrice} precision={instrument.pricePrecision} />
          </dd>
        </div>
      </dl>
    </div>
  );
};
